import { parseEmoji } from '@/helper/utils';
import { usePermission } from '@/hooks/usePermission';
import {
  fetchDashboardInfo,
  fetchRecentActivity,
} from '@/store/slices/dashboardManagementSlice/dashboardManagementSlice';
import {
  BellRing,
  Clipboard,
  FilePenLine,
  FolderClosed,
  Plus,
  SendHorizontal,
  UserPlus,
  Users,
} from 'lucide-react';
import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AppDispatch, RootState } from '../store';
import { fetchProjectsList } from '../store/slices/projectsSlice/projectsSlice';
import QuickAction, { QuickActionProps } from './CommonComponents/QuickAction';
import RecentActivity from './CommonComponents/RecentActivity';
import StatCard from './CommonComponents/StatCard';
import PageHeaderWrapper from './Layout/PageHeaderWrapper';
import ProjectCard from './Projects/ProjectCard';

const Dashboard: React.FC = (action) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const projectsData = useSelector((state: RootState) => state.projects.list.data);
  const { list: gridCount, recentActivity } = useSelector((state: RootState) => state.dashboard);
  const hasFetched = useRef(false);
  const isMounted = useRef(true);

  // Permission checks
  const canCreateSurvey = usePermission('create', 'survey');
  const canAddUser = usePermission('create', 'user');
  const canCreateProject = usePermission('create', 'project');
  const canSendNotification = usePermission('create', 'notification');

  const getActivityIcon = (action: string, entityType: string) => {
    const entity = entityType?.toLowerCase();

    if (entity === 'notifications') return <BellRing />;
    if (entity === 'project' || entity === 'projects') return <FolderClosed />;
    if (entity === 'survey' || entity === 'surveys') return <Clipboard />;
    if (entity === 'user' || entity === 'users') return <Users />;

    return <Users />;
  };

  const getActivityColor = (action: string, entityType: string) => {
    const entity = entityType?.toLowerCase();

    if (entity === 'notifications') return 'bg-[#F4A12C80]';
    if (entity === 'project' || entity === 'projects') return 'bg-[#5CC9A780]';
    if (entity === 'survey' || entity === 'surveys') return 'bg-[#E86A9B80]';
    if (entity === 'user' || entity === 'users') return 'bg-[#4C86E780]';

    return 'bg-[#4C86E780]';
  };

  const getActivityTitle = (action: string, entityType: string) => {
    const actionText = action?.toLowerCase();
    const entity = entityType?.replace(/s$/, '')?.toLowerCase();

    const actionMap: Record<string, string> = {
      create: 'created',
      update: 'updated',
      delete: 'deleted',
      read: 'viewed',
    };

    const displayAction = actionMap[actionText] || actionText;
    return `${entity?.charAt(0).toUpperCase()}${entity?.slice(1)} ${displayAction}`;
  };

  const getTimeAgo = (createdAt: string) => {
    const now = new Date();
    const created = new Date(createdAt);
    const diffMs = now.getTime() - created.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;

    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      dispatch(fetchDashboardInfo());
      dispatch(fetchProjectsList({}));
      dispatch(fetchRecentActivity());
    }

    // Cleanup function to prevent race conditions
    return () => {
      isMounted.current = false;
    };
  }, []);

  const QuickActions: QuickActionProps[] = [
    {
      title: 'Create Survey',
      icon: <Plus />,
      color: 'bg-[#E86A9B80]',
      disabled: !canCreateSurvey,
      className: !canCreateSurvey ? 'opacity-40 cursor-not-allowed' : '',
      onClick: () =>
        navigate('/dashboard/survey-management/create', {
          state: { from: 'dashboard' },
        }),
    },
    {
      title: 'Add User',
      icon: <UserPlus />,
      color: 'bg-[#4C86E780]',
      disabled: !canAddUser,
      className: !canAddUser ? 'opacity-40 cursor-not-allowed' : '',
      onClick: () =>
        navigate('/dashboard/system-admin-users/create', {
          state: { from: 'dashboard' },
        }),
    },
    {
      title: 'New Project',
      icon: <FilePenLine />,
      color: 'bg-[#5CC9A780]',
      disabled: !canCreateProject,
      className: !canCreateProject ? 'opacity-40 cursor-not-allowed' : '',
      onClick: () =>
        navigate('/dashboard/project-management/create', {
          state: { from: 'dashboard' },
        }),
    },
    {
      title: 'Send Notification',
      icon: <SendHorizontal />,
      color: 'bg-[#F4A12C80]',
      disabled: !canSendNotification,
      className: !canSendNotification ? 'opacity-40 cursor-not-allowed' : '',
      onClick: () =>
        navigate('/dashboard/notification-management/create', {
          state: { from: 'dashboard' },
        }),
    },
  ];

  const RecentActivityCard =
    recentActivity?.data && Array.isArray(recentActivity.data) && recentActivity.data.length > 0
      ? recentActivity.data.slice(0, 3).map((activity: any) => ({
          color: getActivityColor(activity.action, activity.entityType),
          icon: getActivityIcon(activity.action, activity.entityType),
          title: getActivityTitle(activity.action, activity.entityType),
          time: getTimeAgo(activity.createdAt),
        }))
      : [];

  const fallbackActivities = [
    {
      color: 'bg-[#4C86E780] text-[#2B1C25]',
      icon: <Users />,
      title: 'New user registered',
      time: '2 mins ago',
    },
    {
      color: 'bg-[#E86A9B80]',
      icon: <Clipboard />,
      title: 'Survey completed',
      time: '15 mins ago',
    },
    {
      color: 'bg-[#5CC9A780]',
      icon: <FolderClosed className="w-6 h-6" />,
      title: 'New project created',
      time: '30 mins ago',
    },
  ];

  const Stats = [
    {
      id: 1,
      title: 'Total Users',
      value: (gridCount.data.totalUsers as any)?.totalUser ?? 0,
      change: `${(gridCount.data.totalUsers as any)?.percentage ?? 0}% from last month`,
      icon: Users,
      iconBg: 'bg-[#4C86E780]',
      changeColor: ((gridCount.data.totalUsers as any)?.percentage ?? 0) >= 0 ? 'green' : 'red',
    },
    {
      id: 2,
      title: 'Active Surveys',
      value: (gridCount.data.totalSurvey as any)?.totalSurveySets ?? 0,
      change: `${(gridCount.data.totalSurvey as any)?.growthPercent ?? 0}% from last month`,
      icon: Clipboard,
      iconBg: 'bg-[#E86A9B80]',
      changeColor: ((gridCount.data.totalSurvey as any)?.growthPercent ?? 0) >= 0 ? 'green' : 'red',
    },
    {
      id: 3,
      title: 'Projects',
      value: (gridCount.data.totalProject as any)?.totalProjects ?? 0,
      change: `${(gridCount.data.totalProject as any)?.growthPercent ?? 0}% from last month`,
      icon: FolderClosed,
      iconBg: 'bg-[#5CC9A780]',
      changeColor:
        ((gridCount.data.totalProject as any)?.growthPercent ?? 0) >= 0 ? 'green' : 'red',
    },
    {
      id: 4,
      title: 'Notifications',
      value: (gridCount.data.totalNotification as any)?.totalCount ?? 0,
      change: `${(gridCount.data.totalNotification as any)?.growthPercent ?? 0}% from last month`,
      icon: BellRing,
      iconBg: 'bg-[#F4A12C80]',
      changeColor:
        ((gridCount.data.totalNotification as any)?.growthPercent ?? 0) >= 0 ? 'green' : 'red',
    },
  ];

  return (
    <PageHeaderWrapper
      title="Dashboard"
      description="Welcome back! Here's what's happening today."
      showSearch
      showBell
      showSettings
      // onSearch={() => showToast.warning('Coming soon!')}   functionality coming soon
      // onBell={() => alert('Bell clicked')}
      // onSettings={() => showToast.warning('Coming soon!')}  functionality coming soon
      showActions={false}
    >
      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        {Stats.map((stat) => (
          <StatCard
            key={stat.id}
            icon={stat.icon}
            iconBg={stat.iconBg}
            title={stat.title}
            value={stat.value}
            change={stat.change}
            changeColor={stat.changeColor as 'green' | 'red'}
          />
        ))}
      </div>

      {/* Activities and Quick Actions */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {/* Activities */}
        <div className="shadow rounded-xl p-4 bg-[#F2EBEE]">
          <h2 className="text-lg font-Lato font-bold mb-4 text-[#621C43]">Recent Activity</h2>
          <div className="space-y-3">
            {((RecentActivityCard?.length ?? 0) > 0 ? RecentActivityCard : fallbackActivities).map(
              (activity: any, index: number) => (
                <RecentActivity
                  key={index}
                  color={activity.color}
                  icon={activity.icon}
                  title={activity.title}
                  time={activity.time}
                />
              ),
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="shadow rounded-xl p-4 bg-[#F2EBEE]">
          <h2 className="text-lg font-Lato font-bold mb-4 text-[#621C43]">Quick Actions</h2>
          <div className="grid grid-cols-2 gap-3">
            {QuickActions.map((action) => (
              <QuickAction
                key={action.title}
                title={action.title}
                icon={action.icon}
                color={action.color}
                disabled={action.disabled}
                onClick={action.onClick}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Projects */}
      <div className="pb-6">
        {projectsData && projectsData?.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {projectsData?.slice(0, 4)?.map((project) => (
              <ProjectCard
                key={project.id}
                action={project}
                getEmoji={parseEmoji}
                navigate={navigate}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <p className="text-lg">No projects available. Please create a new project.</p>
          </div>
        )}
      </div>
    </PageHeaderWrapper>
  );
};

export default React.memo(Dashboard);
